import { ArrowRightToLine, ListChecks, MessageSquare, Pencil, Phone, Pin, UserMinus, UserPlus } from 'lucide-react'
import { displayName } from '@/store/social'
import { userCardHandler, userContextHandler } from '@/store/contextMenu'
import { formatDateTime } from '@/lib/format'
import { MessageType, type Message, type User } from '@/types/api'
import styles from './SystemMessage.module.css'

/** Системные сообщения группы: добавили, вышел или исключили, переименовали. */
function callDuration(from: Date, to: Date): string {
  const sec = Math.max(0, Math.round((to.getTime() - from.getTime()) / 1000))
  if (sec < 60) return `${sec} с`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} мин`
  return `${Math.floor(min / 60)} ч ${min % 60} мин`
}

export function SystemMessage({ message, users, guildId, onOpenThread, onJump }: { message: Message; users: Record<string, User>; guildId?: string | undefined; onOpenThread?: ((threadId: string) => void) | undefined; onJump?: ((messageId: string) => void) | undefined }) {
  const actor = users[message.author.id] ?? message.author
  const target = message.mentions[0] ? (users[message.mentions[0].id] ?? message.mentions[0]) : null
  // имя — как у автора обычного сообщения: щелчок открывает карточку, правая кнопка — меню
  const person = (u: User) => (
    <strong className={styles.user} onClick={userCardHandler(u.id, { user: u, guildId })} onContextMenu={userContextHandler(u.id, { user: u, guildId })}>
      {displayName(u)}
    </strong>
  )
  const a = person(actor)
  const t = target ? person(target) : null
  let icon = <UserPlus size={16} />
  let text: React.ReactNode
  switch (message.type) {
    case MessageType.RecipientAdd:
      text = <>{a} добавил(а) {t} в группу.</>
      break
    case MessageType.RecipientRemove:
      icon = <UserMinus size={16} />
      text = target && target.id !== actor.id ? <>{a} исключил(а) {t} из группы.</> : <>{a} покинул(а) группу.</>
      break
    case MessageType.UserJoin:
      icon = <ArrowRightToLine size={16} />
      text = <>{a} теперь на сервере.</>
      break
    case MessageType.ThreadCreated:
      icon = <MessageSquare size={16} />
      text = (
        <>
          {a} начал(а) ветку{' '}
          {message.thread && onOpenThread ? (
            <button type="button" className={styles.link} onClick={() => onOpenThread(message.id)}>«{message.content}»</button>
          ) : (
            <strong>«{message.content}»</strong>
          )}
        </>
      )
      break
    case MessageType.Call: {
      icon = <Phone size={16} />
      const ended = message.call?.ended_timestamp
      const duration = ended ? callDuration(new Date(message.timestamp), new Date(ended)) : null
      const n = message.call?.participants.length ?? 0
      text = ended ? (
        <>{a} начал(а) звонок · длился {duration}{n > 1 ? `, ${n} участника(ов)` : ', без ответа'}</>
      ) : (
        <>{a} начал(а) звонок</>
      )
      break
    }
    case MessageType.ChannelPinned: {
      icon = <Pin size={16} />
      const ref = message.message_reference?.message_id
      text = (
        <>
          {a} закрепил(а){' '}
          {ref && onJump ? <button type="button" className={styles.link} onClick={() => onJump(ref)}>сообщение</button> : 'сообщение'}
          {' '}в этом канале.
        </>
      )
      break
    }
    case MessageType.PollResult: {
      icon = <ListChecks size={16} />
      const field = (name: string) => message.embeds?.[0]?.fields?.find((f) => f.name === name)?.value
      const question = field('poll_question_text') ?? ''
      const total = Number(field('total_votes') ?? 0)
      const winner = field('victor_answer_text')
      const winnerVotes = Number(field('victor_answer_votes') ?? 0)
      const emoji = field('victor_answer_emoji_id') ? null : field('victor_answer_emoji_name')
      const ref = message.message_reference?.message_id
      const pct = total ? Math.round((winnerVotes / total) * 100) : 0
      text = (
        <>
          Итоги опроса <strong>«{question}»</strong>:{' '}
          {total === 0 ? 'никто не проголосовал.' : winner ? <>победил вариант <strong>{emoji ? `${emoji} ` : ''}{winner}</strong> — {pct} %.</> : 'голоса разделились поровну.'}
          {ref && onJump ? <>{' '}<button type="button" className={styles.link} onClick={() => onJump(ref)}>Показать опрос</button></> : null}
        </>
      )
      break
    }
    case MessageType.ChannelNameChange:
      icon = <Pencil size={16} />
      text = message.content ? <>{a} изменил(а) название группы: <strong>{message.content}</strong></> : <>{a} убрал(а) название группы.</>
      break
    default:
      text = message.content
  }
  return (
    <div className={styles.row} role="status">
      <span className={styles.icon} aria-hidden="true">{icon}</span>
      {/* время идёт следом за текстом, как в Discord: на узком экране переносится целиком, а не ломается на дату и часы */}
      <span className={styles.text}>
        {text}
        <time className={styles.time} dateTime={message.timestamp}>{formatDateTime(new Date(message.timestamp))}</time>
      </span>
    </div>
  )
}
